/**
 * CompleteOnboarding
 *
 * Customer-facing page where users can fill in whatever is missing from
 * their profile: address, emergency contact, and/or driver's licence photos.
 *
 * Route: /complete/
 * Access: any customer who has a valid OTP session (or lands via admin link)
 *
 * Customers update their own PocketBase record directly using their OTP auth
 * token — no admin credentials needed.
 */

import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { pb, updateVehicleDocument, updateCustomerProfile, prepareCompletionAgreement, getCustomerLease, logErrorToDb } from '../api-utils'
import { appendHelcimPayIframe, setupPaymentEventListener } from '../payment-utils'
import { useSignedAgreementFlow } from './forms/StorageForm/hooks/useSignedAgreementFlow'
import { usePaymentCompletion } from './forms/StorageForm/hooks/usePaymentCompletion'
import { useLoginModal } from '../contexts/LoginModalContext'

// ── Types ──────────────────────────────────────────────────────────────────

interface CustomerRecord {
  id: string
  first_name: string
  last_name: string
  email: string
  phone: string
  address_line_1: string
  address_line_2: string
  city: string
  state: string
  zip: string
  emergency_contact_name: string
  emergency_contact_phone: string
  driver_lic_front: string
  driver_lic_back: string
  status: string
}

type MissingField = 'address' | 'emergency_contact' | 'dl_front' | 'dl_back'

// ── Helpers ────────────────────────────────────────────────────────────────

function getMissing(c: CustomerRecord): MissingField[] {
  const m: MissingField[] = []
  if (!c.address_line_1) m.push('address')
  if (!c.emergency_contact_name) m.push('emergency_contact')
  if (!c.driver_lic_front) m.push('dl_front')
  if (!c.driver_lic_back) m.push('dl_back')
  return m
}

// ── Styles (inline, matching portal palette) ───────────────────────────────

const S = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #232043 0%, #3a3068 100%)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '4rem 1rem 3rem',
  } as React.CSSProperties,

  card: {
    background: '#fff',
    borderRadius: 12,
    padding: '2.5rem 2rem',
    width: '100%',
    maxWidth: 540,
    boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
  } as React.CSSProperties,

  heading: {
    color: '#232043',
    fontSize: '1.6rem',
    fontWeight: 700,
    marginBottom: '0.4rem',
  } as React.CSSProperties,

  sub: {
    color: '#666',
    fontSize: '0.95rem',
    marginBottom: '2rem',
  } as React.CSSProperties,

  sectionTitle: {
    color: '#232043',
    fontSize: '1rem',
    fontWeight: 700,
    margin: '1.5rem 0 0.75rem',
    paddingTop: '1.25rem',
    borderTop: '1px solid #eee',
  } as React.CSSProperties,

  label: {
    display: 'block',
    fontSize: '0.83rem',
    fontWeight: 600,
    color: '#555',
    marginBottom: '0.3rem',
    marginTop: '0.75rem',
  } as React.CSSProperties,

  input: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1px solid #ddd',
    borderRadius: 6,
    fontSize: '0.95rem',
    boxSizing: 'border-box',
    color: '#333',
    outline: 'none',
    transition: 'border-color 0.15s',
  } as React.CSSProperties,

  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.75rem',
  } as React.CSSProperties,

  fileZone: (hasFile: boolean) => ({
    border: `2px dashed ${hasFile ? '#43a047' : '#ccc'}`,
    borderRadius: 8,
    padding: '1rem',
    textAlign: 'center' as const,
    cursor: 'pointer',
    background: hasFile ? '#f1f8f1' : '#fafafa',
    marginTop: '0.5rem',
    transition: 'all 0.15s',
  }),

  fileLabel: {
    fontSize: '0.85rem',
    color: '#888',
  } as React.CSSProperties,

  fileSelected: {
    fontSize: '0.85rem',
    color: '#2e7d32',
    fontWeight: 600,
  } as React.CSSProperties,

  alreadyUploaded: {
    fontSize: '0.82rem',
    color: '#2e7d32',
    marginTop: '0.4rem',
  } as React.CSSProperties,

  btn: {
    display: 'block',
    width: '100%',
    padding: '0.85rem',
    background: '#232043',
    color: 'yellow',
    border: 'none',
    borderRadius: 8,
    fontSize: '1rem',
    fontWeight: 700,
    letterSpacing: 1,
    cursor: 'pointer',
    marginTop: '2rem',
  } as React.CSSProperties,

  btnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  } as React.CSSProperties,

  error: {
    background: '#fdecea',
    color: '#c62828',
    padding: '0.75rem 1rem',
    borderRadius: 6,
    fontSize: '0.9rem',
    marginTop: '1rem',
  } as React.CSSProperties,

  success: {
    background: '#e8f5e9',
    color: '#2e7d32',
    padding: '0.75rem 1rem',
    borderRadius: 6,
    fontSize: '0.9rem',
    marginTop: '1rem',
  } as React.CSSProperties,

  loginPrompt: {
    textAlign: 'center' as const,
  },

  loginTitle: {
    color: '#232043',
    fontSize: '1.5rem',
    fontWeight: 700,
    marginBottom: '0.75rem',
  } as React.CSSProperties,

  loginText: {
    color: '#555',
    fontSize: '0.95rem',
    marginBottom: '1.5rem',
  } as React.CSSProperties,

  loginBtn: {
    background: '#232043',
    color: 'yellow',
    border: 'none',
    borderRadius: 8,
    padding: '0.75rem 2rem',
    fontSize: '1rem',
    fontWeight: 700,
    cursor: 'pointer',
    letterSpacing: 1,
  } as React.CSSProperties,
}

// ── Component ──────────────────────────────────────────────────────────────

export default function CompleteOnboarding() {
  const { setIsLoginModalOpen } = useLoginModal()

  const [isAuthenticated, setIsAuthenticated] = useState(pb.authStore.isValid)
  const [customer, setCustomer] = useState<CustomerRecord | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [missing, setMissing] = useState<MissingField[]>([])

  // Form state
  const [addressLine1, setAddressLine1] = useState('')
  const [addressLine2, setAddressLine2] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zip, setZip] = useState('')
  const [emergencyName, setEmergencyName] = useState('')
  const [emergencyPhone, setEmergencyPhone] = useState('')
  const [dlFront, setDlFront] = useState<File | null>(null)
  const [dlBack, setDlBack] = useState<File | null>(null)
  // Proof of insurance lives on the vehicle (admin-only collection), so we
  // can't read its current state from here — always offer the upload.
  const [poi, setPoi] = useState<File | null>(null)
  const [poiSaved, setPoiSaved] = useState(false)

  const dlFrontRef = useRef<HTMLInputElement>(null)
  const dlBackRef = useRef<HTMLInputElement>(null)
  const poiRef = useRef<HTMLInputElement>(null)

  // ── Sign + pay flow (reuses the reservation flow's hooks) ──────────────────
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const hasSigned = searchParams.get('hasSigned') === 'true'
  const invoiceNumber = searchParams.get('invoiceNumber')
  const paidCustomerId = searchParams.get('customerCode')
  const paidAmount = searchParams.get('amount')
  const transactionId = searchParams.get('transactionId')
  const paidDate = searchParams.get('paidDate')
  const payMethod = searchParams.get('payMethod')

  const [lease, setLease] = useState<any>(null)
  const [space, setSpace] = useState<any>(null)
  const [contractId, setContractId] = useState<string | null>(null)
  const [signPageUrl, setSignPageUrl] = useState<string>()
  const [startingSign, setStartingSign] = useState(false)
  const [flowError, setFlowError] = useState('')

  // Pro-ration anchor persisted across the sign redirect (reservation date, not
  // completion day). Undefined -> the calc falls back to today.
  const prorationDate = (() => {
    const iso = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('completeReservationDate') : null
    return iso ? new Date(iso) : undefined
  })()

  // On return from signing (?hasSigned=true): attach PDF, compute pro-ration,
  // get a Helcim checkout token. Then the effects below render the pay iframe.
  const { checkoutToken, error: signedFlowError } = useSignedAgreementFlow(hasSigned, space, contractId, prorationDate)
  // On return from payment (?invoiceNumber=...): finalize + activate the lease.
  const { error: paymentError } = usePaymentCompletion(
    invoiceNumber, space, paidDate, contractId, paidCustomerId, transactionId, payMethod, paidAmount,
  )

  useEffect(() => {
    if (checkoutToken) appendHelcimPayIframe(checkoutToken)
  }, [checkoutToken])

  useEffect(() => {
    if (!checkoutToken) return
    return setupPaymentEventListener(
      checkoutToken,
      (m: any) => navigate(`/complete?invoiceNumber=${m.invoiceNumber}&customerCode=${m.customerCode}&amount=${m.amount}&transactionId=${m.transactionId}&paidDate=${m.dateCreated}&payMethod=${m.cardHolderName ? 'cc' : 'ach'}`),
      (m: any) => console.error('Payment aborted:', m),
      (m: any) => console.log('Payment cancelled:', m),
    )
  }, [checkoutToken, navigate])

  // Load the customer's lease (space + current contract) so we know what's left.
  useEffect(() => {
    if (!isAuthenticated) return
    const cid = pb.authStore.record?.id
    if (!cid) return
    getCustomerLease(cid)
      .then((r: any) => {
        if (r?.success && r.lease) {
          setLease(r.lease)
          setSpace(r.lease.expand?.spaces?.[0] || null)
          setContractId(r.lease.contract_ref_id || null)
        }
      })
      .catch((e: any) => console.error('Failed to load lease:', e))
  }, [isAuthenticated, invoiceNumber])

  // Kick off signing: regenerate the contract from the DB (no re-typing) and
  // show the sign iframe. eSignatures returns the customer to /complete?hasSigned=true.
  const handleStartSign = async () => {
    setStartingSign(true)
    setFlowError('')
    try {
      const redirect = `${window.location.origin}/complete?hasSigned=true`
      const res: any = await prepareCompletionAgreement({ token: pb.authStore.token, redirect_url: redirect })
      if (!res?.success || !res.sign_page_url) throw new Error(res?.error || 'Could not start signing')
      if (res.reservation_date) {
        sessionStorage.setItem('completeReservationDate', new Date(res.reservation_date).toISOString())
      }
      setContractId(res.contract_id || contractId)
      setSignPageUrl(res.sign_page_url)
      window.scrollTo(0, 0)
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Could not start signing. Please try again.'
      setFlowError(msg)
      logErrorToDb({ event_name: 'complete_onboarding_sign_error', error_message: msg, customer_email: customer?.email, error_details: { status: e?.response?.status } })
    } finally {
      setStartingSign(false)
    }
  }

  // Track auth changes (e.g. after OTP login)
  useEffect(() => {
    const unsub = pb.authStore.onChange(() => {
      setIsAuthenticated(pb.authStore.isValid)
    })
    return unsub
  }, [])

  // Load customer when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false)
      return
    }
    const id = pb.authStore.record?.id
    if (!id) { setLoading(false); return }

    pb.collection('customers').getOne<CustomerRecord>(id)
      .then(c => {
        setCustomer(c)
        setMissing(getMissing(c))
        // Pre-fill existing values
        setAddressLine1(c.address_line_1 || '')
        setAddressLine2(c.address_line_2 || '')
        setCity(c.city || '')
        setState(c.state || '')
        setZip(c.zip || '')
        setEmergencyName(c.emergency_contact_name || '')
        setEmergencyPhone(c.emergency_contact_phone || '')
      })
      .catch(err => {
        console.error('Failed to load customer:', err)
        if (err?.status === 401 || err?.status === 403) {
          pb.authStore.clear()
          setIsAuthenticated(false)
        }
      })
      .finally(() => setLoading(false))
  }, [isAuthenticated])

  const handleSave = async () => {
    setSaving(true)
    setSaveError('')
    setSaveSuccess(false)

    try {
      const id = pb.authStore.record?.id
      if (!id) throw new Error('Not authenticated')

      // Customer-record fields go through a function: the customers collection
      // is api_clients-write-only, so a customer's own pb.update 404s ("resource
      // wasn't found"). The function verifies the OTP token and writes as an
      // api_client; licence images are compressed inside the helper.
      const hasCustomerUpdates = !!(addressLine1 || addressLine2 || city || state || zip || emergencyName || emergencyPhone || dlFront || dlBack)
      if (hasCustomerUpdates) {
        const resp: any = await updateCustomerProfile({
          token: pb.authStore.token,
          address_line_1: addressLine1 || undefined,
          address_line_2: addressLine2 || undefined,
          city: city || undefined,
          state: state ? state.toUpperCase().slice(0, 2) : undefined,
          zip: zip || undefined,
          emergency_contact_name: emergencyName || undefined,
          emergency_contact_phone: emergencyPhone || undefined,
          driver_lic_front: dlFront || undefined,
          driver_lic_back: dlBack || undefined,
        })
        if (!resp?.success) throw new Error(resp?.error || 'Failed to save your profile')
        setCustomer(resp.customer)
        setMissing(getMissing(resp.customer))
      }

      // Proof of insurance goes to the vehicle via a function (vehicles is
      // admin-only). Uses the customer's OTP token to authorize.
      if (poi) {
        await updateVehicleDocument({ token: pb.authStore.token, proof_of_insurance: poi })
        setPoi(null)
        setPoiSaved(true)
      }

      setSaveSuccess(true)
    } catch (err: any) {
      console.error('Save failed:', err)
      const msg = err?.response?.data?.error || err?.message || 'Failed to save. Please try again.'
      setSaveError(msg)
      logErrorToDb({ event_name: 'complete_onboarding_save_error', error_message: msg, customer_email: customer?.email, error_details: { status: err?.response?.status } })
    } finally {
      setSaving(false)
    }
  }

  // ── Not authenticated ────────────────────────────────────────────────────

  if (!isAuthenticated) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <div style={S.loginPrompt}>
            <div style={S.loginTitle}>Complete Your Profile</div>
            <div style={S.loginText}>
              Please log in with the email address associated with your reservation to continue.
            </div>
            <button style={S.loginBtn} onClick={() => setIsLoginModalOpen(true, { redirectOnSuccess: false })}>
              Log In to Continue
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Loading ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <div style={{ color: '#232043', textAlign: 'center', padding: '2rem' }}>Loading your profile…</div>
        </div>
      </div>
    )
  }

  if (!customer) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <div style={{ color: '#c62828', textAlign: 'center', padding: '2rem' }}>
            We couldn't find your account. Please contact us at (614) 699-2495.
          </div>
        </div>
      </div>
    )
  }

  // ── Completion state machine: docs → signature → payment ──────────────────
  const docsComplete = missing.length === 0
  const isSigned = !!(lease && (lease.signed_lease || lease.status === 'signed' || lease.status === 'active'))
  const isPaid = !!(lease && lease.status === 'active') || !!invoiceNumber
  const needsSignature = !!lease && !isSigned
  const needsPayment = !!lease && !isPaid

  // Payment finished (or lease already active) → all done.
  if ((invoiceNumber || (lease && lease.status === 'active')) ) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
            <div style={S.heading}>You're all set, {customer.first_name}!</div>
            <div style={S.sub}>{paymentError ? paymentError : 'Your lease is signed and your first payment is complete. Welcome to Gahanna RV & Boat Storage!'}</div>
            <a href="/customer" style={{ color: '#232043', fontWeight: 600, fontSize: '0.95rem' }}>View My Account →</a>
          </div>
        </div>
      </div>
    )
  }

  // Helcim checkout iframe is appended to <body> (fixed, full-screen) once we
  // have a token; show a light placeholder behind it.
  if (checkoutToken) {
    return (
      <div style={S.page}><div style={S.card}><div style={{ textAlign: 'center' }}>
        <div style={S.heading}>Opening secure payment…</div>
        <div style={S.sub}>Complete your payment in the secure window.</div>
      </div></div></div>
    )
  }

  // Just signed — attaching the PDF and preparing payment.
  if (hasSigned) {
    return (
      <div style={S.page}><div style={S.card}><div style={{ textAlign: 'center' }}>
        <div style={S.heading}>Finalizing your lease…</div>
        <div style={S.sub}>{signedFlowError ? signedFlowError : 'Preparing your first payment. This only takes a moment.'}</div>
      </div></div></div>
    )
  }

  // Signing iframe active.
  if (signPageUrl) {
    return (
      <div style={S.page}><div style={{ ...S.card, maxWidth: 760 }}>
        <div style={S.heading}>Review & sign your lease</div>
        <div style={S.sub}>Everything is filled in from your reservation — just review and sign below.</div>
        <iframe src={signPageUrl} title="Sign your lease" style={{ width: '100%', height: '75vh', border: 'none', borderRadius: 8, marginTop: '1rem' }} />
      </div></div>
    )
  }

  // Sign and pay come FIRST — documents are optional and can be added later, so
  // they never block the customer from signing + paying.
  if (needsSignature) {
    return (
      <div style={S.page}><div style={S.card}><div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✍️</div>
        <div style={S.heading}>Sign your lease</div>
        <div style={S.sub}>It's already filled in from your reservation — just review and sign, there's nothing to re-type. You can add your documents later.</div>
        {flowError && <div style={S.error}>{flowError}</div>}
        <button style={{ ...S.btn, ...(startingSign ? S.btnDisabled : {}) }} onClick={handleStartSign} disabled={startingSign}>
          {startingSign ? 'Preparing…' : 'Review & Sign'}
        </button>
      </div></div></div>
    )
  }
  if (needsPayment) {
    return (
      <div style={S.page}><div style={S.card}><div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>💳</div>
        <div style={S.heading}>Last step: your first payment</div>
        <div style={S.sub}>Your lease is signed. Complete your pro-rated first payment to finish.</div>
        {flowError && <div style={S.error}>{flowError}</div>}
        <button style={S.btn} onClick={() => navigate('/complete?hasSigned=true')}>Pay Now</button>
      </div></div></div>
    )
  }

  // Docs done and no outstanding lease steps (or no lease on file) → all set.
  if (docsComplete && !saveSuccess) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✓</div>
            <div style={S.heading}>You're all set, {customer.first_name}!</div>
            <div style={S.sub}>Your profile is complete. No further action is needed.</div>
            <a href="/customer" style={{ color: '#232043', fontWeight: 600, fontSize: '0.95rem' }}>
              View My Account →
            </a>
          </div>
        </div>
      </div>
    )
  }

  // ── Form ─────────────────────────────────────────────────────────────────

  const canSave =
    !saving && (
      (missing.includes('address') && addressLine1 && city && state && zip) ||
      (missing.includes('emergency_contact') && emergencyName && emergencyPhone) ||
      (missing.includes('dl_front') && dlFront !== null) ||
      (missing.includes('dl_back') && dlBack !== null) ||
      // also allow saving partial updates even if not missing
      dlFront || dlBack || poi || emergencyName || addressLine1
    )

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.heading}>
          {saveSuccess && missing.length === 0 ? 'You\'re all set!' : `Hi ${customer.first_name}, just a few more details`}
        </div>
        <div style={S.sub}>
          {saveSuccess && missing.length === 0
            ? 'Your profile is now complete.'
            : `We need the following information to finalize your account.`}
        </div>

        {saveSuccess && (
          <div style={S.success}>
            Profile updated successfully!{' '}
            {missing.length > 0 ? 'Please fill in the remaining fields below.' : ''}
          </div>
        )}

        {/* ── Address ──────────────────────────────────────────────────── */}
        {(missing.includes('address') || saveSuccess) && (
          <>
            <div style={S.sectionTitle}>Address</div>
            <label style={S.label}>Street Address *</label>
            <input
              style={S.input}
              value={addressLine1}
              onChange={e => setAddressLine1(e.target.value)}
              placeholder="123 Main St"
            />
            <label style={S.label}>Apt / Suite (optional)</label>
            <input
              style={S.input}
              value={addressLine2}
              onChange={e => setAddressLine2(e.target.value)}
              placeholder="Apt 4B"
            />
            <div style={S.row}>
              <div>
                <label style={S.label}>City *</label>
                <input style={S.input} value={city} onChange={e => setCity(e.target.value)} placeholder="Columbus" />
              </div>
              <div>
                <label style={S.label}>State *</label>
                <input style={S.input} value={state} onChange={e => setState(e.target.value)} placeholder="OH" maxLength={2} />
              </div>
            </div>
            <label style={S.label}>Zip Code *</label>
            <input style={S.input} value={zip} onChange={e => setZip(e.target.value)} placeholder="43230" maxLength={10} />
          </>
        )}

        {/* ── Emergency Contact ─────────────────────────────────────────── */}
        {(missing.includes('emergency_contact') || saveSuccess) && (
          <>
            <div style={S.sectionTitle}>Emergency Contact</div>
            <label style={S.label}>Name *</label>
            <input
              style={S.input}
              value={emergencyName}
              onChange={e => setEmergencyName(e.target.value)}
              placeholder="Jane Doe"
            />
            <label style={S.label}>Phone *</label>
            <input
              style={S.input}
              type="tel"
              value={emergencyPhone}
              onChange={e => setEmergencyPhone(e.target.value)}
              placeholder="(614) 555-0100"
            />
          </>
        )}

        {/* ── Driver's Licence ─────────────────────────────────────────── */}
        {(missing.includes('dl_front') || missing.includes('dl_back')) && (
          <>
            <div style={S.sectionTitle}>Driver's Licence</div>
            <p style={{ color: '#666', fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
              Please upload a photo of the front and back of your driver's licence.
              Accepted: JPG, PNG, HEIC.
            </p>

            {(missing.includes('dl_front') || customer.driver_lic_front) && (
              <>
                <label style={S.label}>Front of Licence</label>
                {customer.driver_lic_front && !dlFront && (
                  <div style={S.alreadyUploaded}>✓ Already uploaded — tap below to replace</div>
                )}
                <div
                  style={S.fileZone(!!dlFront)}
                  onClick={() => dlFrontRef.current?.click()}
                >
                  {dlFront
                    ? <span style={S.fileSelected}>✓ {dlFront.name}</span>
                    : <span style={S.fileLabel}>Tap to choose a photo</span>}
                </div>
                <input
                  ref={dlFrontRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={e => setDlFront(e.target.files?.[0] || null)}
                />
              </>
            )}

            {(missing.includes('dl_back') || customer.driver_lic_back) && (
              <>
                <label style={{ ...S.label, marginTop: '1rem' }}>Back of Licence</label>
                {customer.driver_lic_back && !dlBack && (
                  <div style={S.alreadyUploaded}>✓ Already uploaded — tap below to replace</div>
                )}
                <div
                  style={S.fileZone(!!dlBack)}
                  onClick={() => dlBackRef.current?.click()}
                >
                  {dlBack
                    ? <span style={S.fileSelected}>✓ {dlBack.name}</span>
                    : <span style={S.fileLabel}>Tap to choose a photo</span>}
                </div>
                <input
                  ref={dlBackRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={e => setDlBack(e.target.files?.[0] || null)}
                />
              </>
            )}
          </>
        )}

        {/* ── Proof of Insurance ────────────────────────────────────────── */}
        <div style={S.sectionTitle}>Proof of Insurance</div>
        <p style={{ color: '#666', fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
          Upload a photo or PDF of your current proof of insurance for the stored vehicle.
          Accepted: JPG, PNG, HEIC, PDF.
        </p>
        {poiSaved && !poi && (
          <div style={S.alreadyUploaded}>✓ Uploaded — tap below to replace</div>
        )}
        <div style={S.fileZone(!!poi)} onClick={() => poiRef.current?.click()}>
          {poi
            ? <span style={S.fileSelected}>✓ {poi.name}</span>
            : <span style={S.fileLabel}>Tap to choose a file</span>}
        </div>
        <input
          ref={poiRef}
          type="file"
          accept="image/*,application/pdf"
          style={{ display: 'none' }}
          onChange={e => setPoi(e.target.files?.[0] || null)}
        />

        {saveError && <div style={S.error}>{saveError}</div>}

        {(missing.length > 0 || poi) && (
          <button
            style={{ ...S.btn, ...((!canSave || saving) ? S.btnDisabled : {}) }}
            onClick={handleSave}
            disabled={!canSave || saving}
          >
            {saving ? 'Saving…' : 'Save & Continue'}
          </button>
        )}

        {missing.length === 0 && saveSuccess && (
          <a
            href="/customer"
            style={{ ...S.btn, textDecoration: 'none', display: 'block', textAlign: 'center' }}
          >
            Go to My Account
          </a>
        )}
      </div>
    </div>
  )
}
